/**
 * # Implementation guide
 *
 * ## Section scaffold
  <div 
    data-view-more-section-id="{{ section.id }}__view-more"
    class="{% if section.settings.enable_view_more_feature %}view-more__section{% endif %}"
  >
    {%- if section.settings.enable_view_more_feature -%}
      <div data-view-more-overlay class="view-more__overlay"></div>
    {%- endif -%}
    
    ...section content
  </div>
  
  {%- if section.settings.enable_view_more_feature -%}
    <view-more 
      data-view-more-id="{{ section.id }}__view-more"
      hidden
      data-settings='{
        "maxHeight": {{ section.settings.max_height }},
        "moreText": "{{ section.settings.read_more_text }}",
        "lessText": "{{ section.settings.read_less_text }}"
      }'
    >
      <button class="text-with-icon group">
        <span data-view-more-text class="view-more__toggle-text">{{ section.settings.read_more_text -}}</span>
        <span data-view-more-icon class="circle-chevron group-hover:colors">{%- render 'icon' with 'chevron-bottom-small' -%}</span>
      </button>
    </view-more>
  {%- endif -%}
 *
 *
 * ## Assets
  {%- if section.settings.enable_view_more_feature -%}
    <script src="{{ 'view-more.js' | asset_url }}" defer></script>
    
    <style>
      #shopify-section-{{ section.id }} {
        --gradient_0: {{ section.settings.gradient_color | hex_to_rgba: 0 }};
        --gradient_1: {{ section.settings.gradient_color | hex_to_rgba: 1 }};
      }
    </style>
    
    {{- 'view-more.css' | asset_url | stylesheet_tag: preload: true -}}
  {%- endif -%}
 *
 *
 * ## Section settings schema
  {
    "type": "header",
    "content": "'View more' feature"
  },
  {
    "type": "checkbox",
    "id": "enable_view_more_feature",
    "label": "Enable 'View more' feature",
    "default": false
  },
  {
    "type": "color",
    "id": "gradient_color",
    "label": "Gradient Color fade",
    "default": "#F5F5F5"
  },
  {
    "type": "number",
    "id": "max_height",
    "label": "Height of the section to crop",
    "default": 100,
    "info": "Only works if the setting is smaller than the section height"
  },
  {
    "type": "text",
    "id": "read_more_text",
    "label": "Read more button text",
    "default": "Read More"
  },
  {
    "type": "text",
    "id": "read_less_text",
    "label": "Read less button text",
    "default": "Read Less"
  }
 *
 */

class ViewMore extends HTMLElement {
  constructor() {
    super();
    
    this.internalId = this.dataset.viewMoreId;
    this.section = document.querySelector(`[data-view-more-section-id="${ this.internalId }"]`);
    
    this.setHidden();
    
    if (!this.internalId || !this.section) return;
    
    this.overlay = this.section.querySelector('[data-view-more-overlay]');
    this.viewMoreTextEl = this.querySelector('[data-view-more-text]');
    
    this.settings = JSON.parse(this.dataset.settings);
    this.sectionInitialHeight = this.section.clientHeight;
    
    this.init();
  }
  
  init() {
    if (this.sectionInitialHeight > this.getMaxHeight()) {
      this.setHidden(false);
      
      this.section.style.height = `${this.getMaxHeight()}px`; 
      this.section.style.overflow = 'hidden';
    } else {
      if (this.overlay) {
        this.overlay.hidden = true;
      }
    }
    
    this.addEventListener('click', this.onToggle);
  }
  
  onToggle() {
    if (this.classList.contains('is-showing')) {
      this.hide();
    } else {
      this.show();
    }
  }
  
  show() {
    this.classList.add('is-showing');
    
    if (this.viewMoreTextEl) {
      this.viewMoreTextEl.innerHTML = this.getLessText();
    }
        
    this.section.animate([
      { height: `${this.getMaxHeight()}px` }, 
      { height: `${this.sectionInitialHeight}px` }
    ], {
      duration: 300,
      easing: 'ease-in-out',
      fill: 'forwards'
    }).onfinish = () => {
      this.section.style.overflow = 'visible';
    };
    
    if (this.overlay) {
      this.overlay.hidden = true;
    }
  }
  
  hide() {
    this.classList.remove('is-showing');
    
    if (this.viewMoreTextEl) {
      this.viewMoreTextEl.innerHTML = this.getMoreText();
    }
    
    this.section.style.overflow = 'hidden';
        
    this.section.animate([
      { height: `${this.sectionInitialHeight}px` }, 
      { height: `${this.getMaxHeight()}px` }
    ], {
      duration: 300,
      easing: 'ease-in-out',
      fill: 'forwards'
    });
    
    if (this.overlay) {
      this.overlay.hidden = false;
    }
  }
  
  setHidden(bool = true) {
    this.hidden = bool;
    
    if (this.overlay) {
      this.overlay.hidden = bool;
    }
  }
  
  getMaxHeight() {
    return this.settings?.maxHeight ?? 150;
  }
  
  getMoreText() {
    return this.settings?.moreText ?? 'Read more';
  }
  
  getLessText() {
    return this.settings?.lessText ?? 'Read less';
  }
}

if (!window.customElements.get('view-more')) {
  window.customElements.define('view-more', ViewMore);
}